import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { CustomFieldValuesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { CustomFieldField, CustomFieldResource, CustomFieldValueFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { EditDialog } from './edit_dialog'
import { Badge } from '../base/badge'

type CustomFieldValue = CustomFieldValuesQuery['customFieldValues']['nodes'][0]

interface Props extends TableProps {
  filters?: CustomFieldValueFilters
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<CustomFieldValuesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openEdit, setOpenEdit } = useTable({
    pageInfo: data?.customFieldValues.pageInfo
  })
  const [customFieldValueId, setCustomFieldValueId] = useState<string>()

  const editCustomFieldValue = (id: string) => {
    if (!updateable) return

    setCustomFieldValueId(id)
    setOpenEdit(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  const valueChecker = (value: CustomFieldValue) => {
    if (value.value == 'true') {
      return <Badge color="green">Yes</Badge>
    } else if (value.value == 'false') {
      return <Badge color="red">No</Badge>
    } else if (value.customField.fieldType == CustomFieldField.Select) {
      return value.customField.options?.find(option => option.id === value.value)?.value
    } else {
      return value.value
    }
  }

  return (
    <>
      <BaseTable<CustomFieldValueFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Field' }, { name: 'Value' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.customFieldValues.nodes = dedupe([
              ...previousData.customFieldValues.nodes,
              ...fetchMoreResult.customFieldValues.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, customFieldNameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          }
        ]}
        rows={data.customFieldValues.nodes.map(customFieldValue => (
          {
            id: customFieldValue.id,
            columns: [
              customFieldValue.customField.name,
              valueChecker(customFieldValue)
            ]
          }
        ))}
        onRowClick={editCustomFieldValue}
      />
      { customFieldValueId && <EditDialog id={customFieldValueId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog
        open={openNew}
        setOpen={setOpenNew}
        defaultValues={{
          resourceId: filters?.resourceIdEq || '',
          resourceType: filters?.resourceTypeEq || CustomFieldResource.AssetType
        }}
      />
    </>
  )
}
